import type {
  EnrichedAction,
  ActionClickStatus,
} from '../../services/recommendations_contracts'
import type { CompletedActionItem, EntityRef, BadgeColor } from '@firstbite/ui'
import { preparedActionLabel, getResolvedBadge } from './resolvedActionMeta'
import { formatRelativeTime } from '../../utils/formatting'
import { SIGNAL_CONFIG } from './recommendationConfig'

/**
 * Day-group heading for a completion timestamp — "Today", "Yesterday", a
 * weekday ("Monday"), or "Mon, Jul 7" (with year when it differs). Mirrors
 * ``groupByCompletionDay`` in RecommendationsCard so the fb-ui panel's day
 * headings match the in-card completion labels. ``now`` is injectable so tests
 * are deterministic without mocking the clock.
 */
export function completionDayLabel(
  resolvedAt: string | null | undefined,
  now: Date = new Date()
): string {
  if (!resolvedAt) return 'Earlier'
  const date = new Date(resolvedAt)
  const yesterday = new Date(now)
  yesterday.setDate(now.getDate() - 1)
  const relative =
    date.toDateString() === now.toDateString()
      ? 'Today'
      : date.toDateString() === yesterday.toDateString()
        ? 'Yesterday'
        : date.toLocaleDateString('en-US', { weekday: 'long' })
  const display = date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    ...(date.getFullYear() === now.getFullYear() ? {} : { year: 'numeric' }),
  })
  return relative !== display ? `${relative}, ${display}` : relative
}

/**
 * Linked records (deal / company / contact) for a completed item, matching
 * ``EntitySummary``'s show rules so the panel mirrors the card footer: the deal
 * shows only for deal-linked signals with a name + id, the company only when
 * there's a bound ContactCompany link, and the contact whenever named.
 */
function entitiesFor(action: EnrichedAction): EntityRef[] {
  const entities: EntityRef[] = []
  const linkTarget = SIGNAL_CONFIG[action.signal_type]?.linkTarget
  if (linkTarget !== 'company' && action.deal_name && action.deal_id) {
    entities.push({
      id: `deal-${action.deal_id}`,
      type: 'deal',
      name: action.deal_name,
      href: `/deals/${action.deal_id}/`,
    })
  }
  if (action.contact_company_id && action.company_link != null) {
    entities.push({
      id: `company-${action.contact_company_id}`,
      type: 'company',
      name: action.company_name,
      href: action.company_link,
    })
  }
  if (action.contact_name) {
    entities.push({
      id: `contact-${action.deal_id ?? action.contact_company_id ?? 'x'}`,
      type: 'contact',
      name: action.contact_name,
    })
  }
  return entities
}

/** Per-action resolution state, sourced from the same click-manager selectors
 *  the card uses so the mapped badge/steps match exactly. */
export interface CompletedItemContext {
  getStatus: (action: EnrichedAction) => ActionClickStatus | null
  getAutoCompleted: (action: EnrichedAction) => boolean
  /** Injectable "now" for deterministic day-label tests. */
  now?: Date
}

/**
 * Map resolved recommendations to the fb-ui ``CompletedActionsPanel`` item
 * model. Newest-first, so same-day items stay contiguous and the panel's
 * first-seen-order day grouping lines up. Applied recs list the executed
 * prepared actions as ``steps``; auto-resolved / dismissed recs carry their
 * system ``resolution_reason`` as the narrative ``description`` — the same
 * split the in-card completed body renders.
 */
export function buildCompletedItems(
  actions: EnrichedAction[],
  { getStatus, getAutoCompleted, now = new Date() }: CompletedItemContext
): CompletedActionItem[] {
  const resolvedTs = (a: EnrichedAction) =>
    a.resolved_at ? new Date(a.resolved_at).getTime() : 0
  return [...actions]
    .sort((x, y) => resolvedTs(y) - resolvedTs(x))
    .map((action, index) => {
      const status = getStatus(action)
      const autoCompleted = getAutoCompleted(action)
      const badge = getResolvedBadge(
        status,
        autoCompleted,
        action.resolution_status
      )
      const wasUserApplied =
        action.resolution_status === 'user_resolved' ||
        (!action.resolution_status && status === 'done' && !autoCompleted)
      const prepared = action.prepared_actions ?? []
      const steps =
        wasUserApplied && prepared.length > 0
          ? prepared.map((pa) =>
              preparedActionLabel(pa, action.signal_type, action.deal_id, true)
            )
          : undefined
      // Applied recs show what was done (steps); everything else shows the
      // system reason. Never both — steps already say it for applied recs.
      const description =
        steps === undefined
          ? (action.resolution_reason ?? undefined)
          : undefined
      const color: BadgeColor = badge?.color === 'neutral' ? 'gray' : 'success'
      return {
        id: action.signal_fingerprint ?? `${action.deal_id}-${index}`,
        group: completionDayLabel(action.resolved_at, now),
        time: action.resolved_at ? formatRelativeTime(action.resolved_at) : '',
        title: action.action,
        status: badge ? { label: badge.label, color } : { label: 'Completed' },
        steps,
        description,
        entities: entitiesFor(action),
      }
    })
}
